import { useEffect, useState } from "react";
import {
  Page,
  Card,
  IndexTable,
  Text,
  Tabs,
  TextField,
  BlockStack,
  Button,
  InlineStack,
  Pagination,
  Badge,
  Link,
} from "@shopify/polaris";
import { SearchIcon } from "@shopify/polaris-icons";
import AppWrapper from "../AppWrapper";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";
import { formatPrice, formatDate, formatPlate } from "../../utility/funcs";
import { TrasladoStatusBadge } from "./trasladoStatus";
import { quoteComparison } from "./trasladoDiff";

const TABS = [
  { id: "all", content: "Todos", statuses: null },
  { id: "active", content: "Activos", statuses: ["draft", "authorized", "in_progress", "completed"] },
];
const PAGE_SIZE = 50;

function Traslados() {
  const { hasPermission } = UserAuth();
  const [tab, setTab] = useState(TABS.findIndex((t) => t.id === "active"));
  const [plate, setPlate] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      let q = supabase
        .from("traslado_orders")
        .select(
          "id, created_at, scheduled_at, status, cobro_cliente, estimate, driver_extra, origin_label, dest_label, origin_cp, dest_cp, planned_km, estimated_total, actual_total, car!inner(id, plate, model(name, brand(name))), driver:service_providers(name), trasladista:trasladistas!traslado_orders_trasladista_fkey(name), secondary:trasladistas!traslado_orders_trasladista_secondary_fkey(name)",
        )
        .order("created_at", { ascending: false })
        .range((page - 1) * PAGE_SIZE, page * PAGE_SIZE - 1);
      const statuses = TABS[tab].statuses;
      if (statuses) q = q.in("status", statuses);
      if (query) q = q.ilike("car.plate", `%${query}%`);
      const { data, error } = await q;
      if (error) console.error(error);
      setRows(data || []);
      setLoading(false);
    };
    load();
  }, [tab, query, page]);

  const route = (r) => {
    const o = r.origin_label || r.origin_cp;
    const d = r.dest_label || r.dest_cp;
    return o || d ? `${o || "?"} → ${d || "?"}` : "—";
  };

  return (
    <AppWrapper>
      <Page
        title="Traslados"
        subtitle="Movimientos de unidades entre ciudades y su costo"
        primaryAction={{
          content: "Nuevo traslado",
          url: "/vehicle_transfers/new",
          disabled: !hasPermission("services.create"),
        }}
        secondaryActions={[
          { content: "Pendientes", url: "/traslados_pendientes" },
          { content: "Trasladistas", url: "/trasladistas" },
          ...(hasPermission("services.pay") ? [{ content: "Tarifas", url: "/traslado_rates" }] : []),
        ]}
      >
        <Card padding="0">
          <Tabs
            tabs={TABS.map((t) => ({ id: t.id, content: t.content }))}
            selected={tab}
            onSelect={(i) => {
              setTab(i);
              setPage(1);
            }}
          />
          <div style={{ padding: 12 }}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setPage(1);
                setQuery(plate.trim().toUpperCase());
              }}
            >
              <InlineStack gap="200" wrap={false}>
                <div style={{ flexGrow: 1, maxWidth: 320 }}>
                  <TextField
                    label="Placa"
                    labelHidden
                    placeholder="Buscar por placa"
                    autoComplete="off"
                    value={plate}
                    onChange={setPlate}
                    clearButton
                    onClearButtonClick={() => {
                      setPlate("");
                      setQuery("");
                    }}
                  />
                </div>
                <Button icon={SearchIcon} submit />
              </InlineStack>
            </form>
          </div>
          <IndexTable
            resourceName={{ singular: "traslado", plural: "traslados" }}
            itemCount={rows.length}
            loading={loading}
            selectable={false}
            headings={[
              { title: "#" },
              { title: "Fecha" },
              { title: "Unidad" },
              { title: "Ruta" },
              { title: "km", alignment: "end" },
              { title: "Trasladista" },
              { title: "Estimado", alignment: "end" },
              { title: "Real", alignment: "end" },
              { title: "Status" },
            ]}
          >
            {rows.map((r, i) => (
              <IndexTable.Row id={String(r.id)} key={r.id} position={i}>
                <IndexTable.Cell>
                  {/* A real link as the row's primary link (same as Cars): the whole
                      row stays clickable, Cmd/Ctrl-click opens a new tab, and the
                      browser's "Abrir en pestaña nueva" works. */}
                  <Link url={`/vehicle_transfers/${r.id}`} removeUnderline monochrome dataPrimaryLink>
                    <Text fontWeight="semibold">{r.id}</Text>
                  </Link>
                </IndexTable.Cell>
                <IndexTable.Cell>{formatDate(r.scheduled_at || r.created_at)}</IndexTable.Cell>
                <IndexTable.Cell>
                  <BlockStack gap="0">
                    <Text fontWeight="semibold">{formatPlate(r.car?.plate)}</Text>
                    <Text tone="subdued" variant="bodySm">
                      {[r.car?.model?.brand?.name, r.car?.model?.name].filter(Boolean).join(" ")}
                    </Text>
                  </BlockStack>
                </IndexTable.Cell>
                <IndexTable.Cell>{route(r)}</IndexTable.Cell>
                <IndexTable.Cell>
                  <Text alignment="end">{r.planned_km ?? "—"}</Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  {r.trasladista?.name ? (
                    <BlockStack gap="0">
                      <Text>{r.trasladista.name}</Text>
                      {r.secondary?.name && (
                        <Text tone="subdued" variant="bodySm">{`+ ${r.secondary.name}`}</Text>
                      )}
                    </BlockStack>
                  ) : (r.driver?.name ? (
                    <Text tone="subdued">{r.driver.name}</Text>
                  ) : "—")}
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <Text alignment="end">
                    {r.estimated_total != null ? formatPrice(r.estimated_total) : "—"}
                  </Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <Text alignment="end">
                    {r.actual_total != null ? formatPrice(r.actual_total) : "—"}
                  </Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                  <InlineStack gap="100" wrap={false}>
                    <TrasladoStatusBadge status={r.status} />
                    {r.cobro_cliente && <Badge tone="info">Con cobro</Badge>}
                    {quoteComparison(r.estimate, r.driver_extra)?.adjusted && (
                      <Badge tone="attention">Ajustado</Badge>
                    )}
                  </InlineStack>
                </IndexTable.Cell>
              </IndexTable.Row>
            ))}
          </IndexTable>
          <div style={{ display: "flex", justifyContent: "center", padding: 12 }}>
            <Pagination
              hasPrevious={page > 1}
              onPrevious={() => setPage((p) => p - 1)}
              hasNext={rows.length === PAGE_SIZE}
              onNext={() => setPage((p) => p + 1)}
            />
          </div>
        </Card>
      </Page>
    </AppWrapper>
  );
}

export default Traslados;
